import Box from "@ui-kit/layout/Box/Box.web";
import IconButton from "@ui-kit/form/IconButton/IconButton.web";
import { isSameDay, Locale, endOfDay } from "date-fns";
import { fr } from "date-fns/locale";
import Cookies from "js-cookie";
import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  DateRange,
  DayPicker,
  Matcher,
  NavProps,
  OnSelectHandler,
} from "react-day-picker";

import { DatePickerMode, DatePickerProps } from "./DatePicker.props";

const BASE_DATE_RANGE: DateRange = { from: undefined, to: undefined };

const DatePicker = ({
  mode,
  selected,
  disablePastDates,
  disableFutureDates,
  customMatcher,
  ...dateProps
}: DatePickerProps): JSX.Element => {
  const nextLocale = Cookies.get("NEXT_LOCALE");
  const locale = useMemo(
    () => (nextLocale === "fr" ? fr : undefined),
    [nextLocale]
  );

  const matcher: Matcher | Matcher[] = useMemo(() => {
    // disable selection of dates before today
    if (disablePastDates) {
      return {
        before: new Date(),
      };
    }
    // disable selection of dates from tomorrow onwards
    if (disableFutureDates) {
      return {
        after: new Date(),
      };
    }

    return customMatcher;
  }, [disableFutureDates, disablePastDates, customMatcher]);
  // Date range selection
  if (mode === DatePickerMode.range) {
    return (
      <DatePicker.Range
        {...dateProps}
        locale={locale as Partial<Locale>}
        selected={(selected as DateRange) || BASE_DATE_RANGE}
        disabled={matcher}
        components={{
          Nav: DatePicker.CustomNav,
        }}
      />
    );
  }

  // Multiple date selection
  if (mode === DatePickerMode.multiple) {
    return (
      <DatePicker.Multiple
        {...dateProps}
        locale={locale as Partial<Locale>}
        selected={selected as Date[]}
        disabled={matcher}
        components={{
          Nav: DatePicker.CustomNav,
        }}
      />
    );
  }

  // Single date selection
  return (
    <DatePicker.Single
      {...dateProps}
      locale={locale as Partial<Locale>}
      selected={selected as Date}
      disabled={matcher}
      components={{
        Nav: DatePicker.CustomNav,
      }}
    />
  );
};

export default DatePicker;

DatePicker.Single = function SingleDatePicker(
  props: Omit<DatePickerProps, "mode">
) {
  const { onChangeSelected } = props;
  const handleSingleSelectDate: OnSelectHandler<Date | undefined> = useCallback(
    (selected, triggerDate, modifiers, e): void => {
      const singleSelection = {
        triggerDate,
        modifiers,
        e,
      };
      onChangeSelected?.({
        ...singleSelection,
        selected: selected || undefined,
      });
    },
    [onChangeSelected]
  );
  return (
    <DayPicker
      {...props}
      mode={DatePickerMode.single}
      // @ts-expect-error onSelect is right prop for the handler, error goes away when using `onSelectHandler` but it that doesnt work
      onSelect={handleSingleSelectDate}
    />
  );
};

DatePicker.Multiple = function MultipleDatePicker(
  props: Omit<DatePickerProps, "mode">
) {
  const { onChangeSelected } = props;
  const handleMultipleSelectDate: OnSelectHandler<Date[] | undefined> =
    useCallback(
      (selected, triggerDate, modifiers, e): void => {
        const multipleSelection = {
          triggerDate,
          modifiers,
          e,
        };
        onChangeSelected?.({
          ...multipleSelection,
          selected: selected || undefined,
        });
      },
      [onChangeSelected]
    );
  return (
    <DayPicker
      mode={DatePickerMode.multiple}
      {...props}
      // @ts-expect-error onSelect has the right type for multiple mode
      onSelect={handleMultipleSelectDate}
    />
  );
};

DatePicker.Range = function RangeDatePicker(
  props: Omit<DatePickerProps, "mode">
) {
  const { onChangeSelected, selected: currentSelected } = props;
  // Track previous from date to detect when user is completing a range vs starting a new one
  const prevFromRef = useRef<Date | undefined>(undefined);

  const handleRangeSelectDate: OnSelectHandler<DateRange | undefined> =
    useCallback(
      (selected, triggerDate, modifiers, e): void => {
        const prevFrom = prevFromRef.current;
        const dateRangeSelection = {
          triggerDate,
          modifiers,
          e,
        };

        // Determine if this click completes a date range:
        // 1. Both from and to dates exist
        // 2. from and to are different dates (not a same-day selection)
        // 3. The from date is unchanged from previous click (this is the "second click")
        const hasCompleteRange =
          selected?.from &&
          selected?.to &&
          !isSameDay(selected.from, selected.to);
        const fromUnchanged =
          prevFrom && selected?.from && isSameDay(prevFrom, selected.from);
        const isRangeComplete = hasCompleteRange && fromUnchanged;

        prevFromRef.current = selected?.from;

        let finalSelected = selected;
        if (selected?.from && selected?.to) {
          finalSelected = { from: selected.from, to: endOfDay(selected.to) };
        }

        onChangeSelected?.({
          ...dateRangeSelection,
          selected: finalSelected || BASE_DATE_RANGE,
          isRangeComplete,
        });
      },
      [onChangeSelected]
    );

  // Sync ref when selected prop changes from outside (e.g., modal re-opens with existing selection)
  // This ensures prevFromRef matches the current selection state
  const currentFrom = (currentSelected as DateRange)?.from;
  useEffect(() => {
    if (
      currentFrom &&
      (!prevFromRef.current || !isSameDay(prevFromRef.current, currentFrom))
    ) {
      prevFromRef.current = currentFrom;
    }
  }, [currentFrom]);

  return (
    // @ts-expect-error type of selected is correct
    <DayPicker
      {...props}
      required // when in range mode, required must be true
      onSelect={handleRangeSelectDate}
      mode={DatePickerMode.range}
    />
  );
};

DatePicker.CustomNav = function CustomNav({
  nextMonth,
  previousMonth,
  onNextClick,
  onPreviousClick,
}: NavProps) {
  return (
    <Box className="rdp-nav">
      <IconButton
        onClick={(e) => previousMonth && onPreviousClick?.(e)}
        iconName="ChevronLeft"
        iconColor="font.dark"
        variant="plain"
        iconOutline={false}
        aria-label="date-picker-nav-prev-button"
        iconSize="24px"
        _hover={{ bg: "gray.300" }}
      />
      <IconButton
        iconName="ChevronRight"
        iconColor="font.dark"
        variant="plain"
        aria-label="date-picker-nav-next-button"
        iconOutline={false}
        onClick={(e) => nextMonth && onNextClick?.(e)}
        iconSize="24px"
        _hover={{ bg: "gray.300" }}
      />
    </Box>
  );
};
